import React from 'react';

import { cn } from '../../utils/cn';

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
type HeadingVariant = 'default' | 'section' | 'subsection' | 'title' | 'subtitle';
type HeadingSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';

interface HeadingProps {
  level: HeadingLevel;
  variant?: HeadingVariant;
  size?: HeadingSize;
  className?: string;
  children: React.ReactNode;
  id?: string;
}

const sizeClasses: Record<HeadingSize, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
  '4xl': 'text-4xl',
  '5xl': 'text-5xl',
};

const variantClasses: Record<HeadingVariant, string> = {
  default: 'font-bold text-gray-900',
  section: 'font-bold text-gray-900 mb-4',
  subsection: 'font-semibold text-gray-800 mb-3',
  title: 'font-bold text-gray-900 mb-6 leading-tight',
  subtitle: 'font-medium text-gray-600 mb-4',
};

/**
 * Componente Heading para mantener una estructura semántica consistente de encabezados
 * Permite definir el nivel del encabezado (h1-h6) y aplicar estilos consistentes
 */
const Heading: React.FC<HeadingProps> = ({
  level,
  variant = 'default',
  size,
  className,
  children,
  id,
  ...props
}) => {
  // Determinar el tamaño basado en el nivel si no se proporciona explícitamente
  const defaultSize: Record<HeadingLevel, HeadingSize> = {
    1: '4xl',
    2: '3xl',
    3: '2xl',
    4: 'xl',
    5: 'lg',
    6: 'md',
  };

  const finalSize = size || defaultSize[level];
  const classes = cn(sizeClasses[finalSize], variantClasses[variant], className);

  const HeadingTag = `h${level}` as keyof JSX.IntrinsicElements;

  return (
    <HeadingTag id={id} className={classes} {...props}>
      {children}
    </HeadingTag>
  );
};

export default Heading;
